import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

function SocialChannelsRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/social/connect?tab=channels${search}`} replace />;
}
import {
  CHOOSE_APP_PATH,
  LEGACY_MODULE_HUB_PATH,
  LEGACY_REGISTER_PATH,
  MODULE_HUB_PATH,
  REGISTER_PATH,
} from '@/constants/authRoutes';
import { FattahLoginPage } from '@/pages/auth/FattahLoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { AuthLayout } from '@/layouts/AuthLayout';
import { MasterAdminLayout } from '@/layouts/MasterAdminLayout';
import { OrgAdminLayout } from '@/layouts/OrgAdminLayout';
import { PosLayout } from '@/layouts/PosLayout';
import { ModuleHubPage } from '@/pages/auth/ModuleHubPage';
import { MasterLoginPage } from '@/pages/auth/MasterLoginPage';
import { OAuthCallbackPage } from '@/pages/auth/OAuthCallbackPage';
import { ModuleLoginPage } from '@/pages/auth/ModuleLoginPage';
import { ModuleRegisterPage } from '@/pages/auth/ModuleRegisterPage';
import { ProtectedRoute, PublicOnlyRoute } from './ProtectedRoute';
import { ModuleScopeGuard } from './ModuleScopeGuard';
import { MODULE_SHARED_MASTER_ROUTES } from './moduleSharedMasterRoutes';
import { getAllModuleLoginRoutes, getAllModuleRegisterRoutes } from '@/config/moduleEntries';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { MasterDashboardPage } from '@/pages/master/MasterDashboardPage';
import { OrganizationsPage } from '@/pages/master/OrganizationsPage';
import { SubscriptionsPage } from '@/pages/master/SubscriptionsPage';
import { PlansPage } from '@/pages/master/PlansPage';
import { AnalyticsPage } from '@/pages/master/AnalyticsPage';
import { MasterSettingsPage } from '@/pages/master/MasterSettingsPage';
import { MasterReferenceDataPage } from '@/pages/master/MasterReferenceDataPage';
import { MasterCustomPlansPage } from '@/pages/master/MasterCustomPlansPage';
import { OrgDashboardPage } from '@/pages/org/OrgDashboardPage';
import { OrganizationProfilePage } from '@/pages/org/OrganizationProfilePage';
import { StoresPage } from '@/pages/org/StoresPage';
import { EmployeesPage } from '@/pages/org/EmployeesPage';
import { RolesPage } from '@/pages/org/RolesPage';
import { ProductsPage } from '@/pages/org/ProductsPage';
import { CategoriesPage } from '@/pages/org/CategoriesPage';
import { BrandsPage } from '@/pages/org/BrandsPage';
import { UnitsPage } from '@/pages/org/UnitsPage';
import { TaxRulesPage } from '@/pages/org/TaxRulesPage';
import { StoreProductsPage } from '@/pages/org/StoreProductsPage';
import { InvoicesPage } from '@/pages/org/InvoicesPage';
import { InventoryPage } from '@/pages/org/InventoryPage';
import { InventoryLedgerPage } from '@/modules/inventory/ledger/InventoryLedgerPage';
import { WarehouseDashboardPage } from '@/modules/warehouse/dashboard/WarehouseDashboardPage';
import { WarehouseOperationsPage } from '@/modules/warehouse/operations/WarehouseOperationsPage';
import { WarehouseSetupPage } from '@/modules/warehouse/setup/WarehouseSetupPage';
import { WarehousePicksPage } from '@/modules/warehouse/picks/WarehousePicksPage';
import { WarehouseTransfersPage } from '@/modules/warehouse/transfers/WarehouseTransfersPage';
import { WarehouseCycleCountsPage } from '@/modules/warehouse/cycle-counts/WarehouseCycleCountsPage';
import { WarehouseWavesPage } from '@/modules/warehouse/waves/WarehouseWavesPage';
import { WarehouseDockPage } from '@/modules/warehouse/dock/WarehouseDockPage';
import { WarehouseCrossDockPage } from '@/modules/warehouse/cross-dock/WarehouseCrossDockPage';
import { WarehouseLaborPage } from '@/modules/warehouse/labor/WarehouseLaborPage';
import { SuppliersPage } from '@/pages/org/SuppliersPage';
import { CustomersPage } from '@/pages/org/CustomersPage';
import { DiscountsPage } from '@/pages/org/DiscountsPage';
import { ReportsPage } from '@/pages/org/ReportsPage';
import { ProductAlertsPage } from '@/pages/org/ProductAlertsPage';
import { ModuleWorkspaceSettingsPage } from '@/pages/org/ModuleWorkspaceSettingsPage';
import { ModuleWorkspaceSubscriptionPage } from '@/pages/org/ModuleWorkspaceSubscriptionPage';
import { AdministrationPage } from '@/pages/org/AdministrationPage';
import { LockedModulePage } from '@/pages/org/LockedModulePage';
import { CrmDashboardPage } from '@/modules/crm/dashboard/CrmDashboardPage';
import { CrmLeadsPage } from '@/modules/crm/leads/CrmLeadsPage';
import { CrmDealsPage } from '@/modules/crm/deals/CrmDealsPage';
import { CrmCompaniesPage } from '@/modules/crm/companies/CrmCompaniesPage';
import { CrmSetupPage } from '@/modules/crm/setup/CrmSetupPage';
import { CrmGoogleOAuthCallbackPage } from '@/modules/crm/setup/CrmGoogleOAuthCallbackPage';
import { CrmContactsPage } from '@/modules/crm/contacts/CrmContactsPage';
import { CrmReportsPage } from '@/modules/crm/reports/CrmReportsPage';
import { Customer360Page } from '@/modules/crm/customers/Customer360Page';
import { CrmWorkspacePage } from '@/modules/crm/shared/CrmWorkspacePage';
import { CrmCampaignsPage } from '@/modules/crm/campaigns/CrmCampaignsPage';
import { CrmTasksPage } from '@/modules/crm/tasks/CrmTasksPage';
import { LeadGenFormsPage } from '@/modules/leadgen/LeadGenFormsPage';
import { LeadGenPublicSubmitPage } from '@/modules/leadgen/LeadGenPublicSubmitPage';
import { LeadGenDashboardPage } from '@/modules/leadgen/LeadGenDashboardPage';
import { LeadGenLandingPagesPage } from '@/modules/leadgen/LeadGenLandingPagesPage';
import { LeadGenCampaignsPage } from '@/modules/leadgen/LeadGenCampaignsPage';
import { LeadGenVisitorTrackingPage } from '@/modules/leadgen/LeadGenVisitorTrackingPage';
import { LeadGenScoringPage } from '@/modules/leadgen/LeadGenScoringPage';
import { LeadGenRoutingPage } from '@/modules/leadgen/LeadGenRoutingPage';
import { LeadGenConsentPage } from '@/modules/leadgen/LeadGenConsentPage';
import { LeadGenReportsPage } from '@/modules/leadgen/LeadGenReportsPage';
import { LeadGenSourcesPage } from '@/modules/leadgen/LeadGenSourcesPage';
import { MarketingDashboardPage } from '@/modules/marketing/MarketingDashboardPage';
import { MarketingCampaignsPage } from '@/modules/marketing/MarketingCampaignsPage';
import { MarketingSegmentsPage } from '@/modules/marketing/MarketingSegmentsPage';
import { MarketingJourneysPage } from '@/modules/marketing/MarketingJourneysPage';
import { MarketingTemplatesPage } from '@/modules/marketing/MarketingTemplatesPage';
import { MarketingLifecyclePage } from '@/modules/marketing/MarketingLifecyclePage';
import { MarketingReportsPage } from '@/modules/marketing/MarketingReportsPage';
import { MarketingEspWebhooksPage } from '@/modules/marketing/MarketingEspWebhooksPage';
import { PortalLoginPage } from '@/modules/portal/PortalLoginPage';
import { PortalVerifyPage } from '@/modules/portal/PortalVerifyPage';
import { PortalDashboardPage } from '@/modules/portal/PortalDashboardPage';
import { SocialDashboardPage } from '@/modules/social/dashboard/SocialDashboardPage';
import { SocialInboxPage } from '@/modules/social/inbox/SocialInboxPage';
import { SocialPublishingCenterPage } from '@/modules/social/publishing/SocialPublishingCenterPage';
import { SocialAnalyticsCenterPage } from '@/modules/social/analytics/SocialAnalyticsCenterPage';
import { SocialAutomationPage } from '@/modules/social/automation/SocialAutomationPage';
import { SocialCrmLeadsPage } from '@/modules/social/crm/SocialCrmLeadsPage';
import { SocialTeamsPage } from '@/modules/social/teams/SocialTeamsPage';
import { SocialSettingsHubPage } from '@/modules/social/settings/SocialSettingsHubPage';
import { SocialCommentsHubPage } from '@/modules/social/comments/SocialCommentsHubPage';
import { PlatformWorkspacePage } from '@/modules/social/platforms/PlatformWorkspacePage';
import { SocialConnectionCenterPage } from '@/modules/social/connection/SocialConnectionCenterPage';
import { PlatformSetupWizardPage } from '@/modules/social/connection/PlatformSetupWizardPage';
import { PlatformGuidePage } from '@/modules/social/connection/PlatformGuidePage';
import { SocialCampaignsPage } from '@/modules/social/campaigns/SocialCampaignsPage';
import { SocialReviewsPage } from '@/modules/social/reviews/SocialReviewsPage';
import { SocialCalendarPage } from '@/modules/social/calendar/SocialCalendarPage';
import { SocialMediaLibraryPage } from '@/modules/social/media/SocialMediaLibraryPage';
import { SocialListeningPage } from '@/modules/social/listening/SocialListeningPage';
import { HrDashboardPage } from '@/modules/hr/dashboard/HrDashboardPage';
import { HrEmployee360Page } from '@/modules/hr/employees/HrEmployee360Page';
import { HrLeavesPage } from '@/modules/hr/leaves/HrLeavesPage';
import { HrRecruitmentPage } from '@/modules/hr/recruitment/HrRecruitmentPage';
import { HrEssPortalPage } from '@/modules/hr/ess/HrEssPortalPage';
import { HrPayrollPage } from '@/modules/hr/payroll/HrPayrollPage';
import { HrStatutoryPage } from '@/modules/hr/statutory/HrStatutoryPage';
import { HrReportsPage } from '@/modules/hr/reports/HrReportsPage';
import { HrFeedback360Page } from '@/modules/hr/feedback/HrFeedback360Page';
import { HrDevicesPage } from '@/modules/hr/devices/HrDevicesPage';
import { HrSetupPage } from '@/modules/hr/setup/HrSetupPage';
import { HrWorkflowsPage } from '@/modules/hr/workflows/HrWorkflowsPage';
import { HrOrgStructurePage } from '@/modules/hr/org/HrOrgStructurePage';
import { HrMssPortalPage } from '@/modules/hr/mss/HrMssPortalPage';
import { HrLeavePoliciesPage } from '@/modules/hr/leaves/HrLeavePoliciesPage';
import { HrShiftsPage } from '@/modules/hr/shifts/HrShiftsPage';
import { HrOnboardingPage } from '@/modules/hr/lifecycle/HrOnboardingPage';
import { HrDocumentsPage } from '@/modules/hr/documents/HrDocumentsPage';
import { HrCompensationPage } from '@/modules/hr/compensation/HrCompensationPage';
import { HrTravelPage } from '@/modules/hr/travel/HrTravelPage';
import { HrSuccessionPage } from '@/modules/hr/succession/HrSuccessionPage';
import { HrAdminConfigPage } from '@/modules/hr/admin/HrAdminConfigPage';
import { HrPublicCareersPage } from '@/modules/hr/careers/HrPublicCareersPage';
import { HrAttendancePage } from '@/modules/hr/attendance/HrAttendancePage';
import { HrTrainingPage } from '@/modules/hr/training/HrTrainingPage';
import { HrClaimsPage } from '@/modules/hr/claims/HrClaimsPage';
import { HrAppraisalPage } from '@/modules/hr/appraisal/HrAppraisalPage';
import { HrApprovalsPage } from '@/modules/hr/approvals/HrApprovalsPage';
import { DeskDashboardPage } from '@/modules/desk/dashboard/DeskDashboardPage';
import { DeskTicketsPage } from '@/modules/desk/tickets/DeskTicketsPage';
import { DeskTicketDetailPage } from '@/modules/desk/tickets/DeskTicketDetailPage';
import { CustomerSupport360Page } from '@/modules/desk/support/CustomerSupport360Page';
import { DeskSetupPage } from '@/modules/desk/setup/DeskSetupPage';
import { DeskKnowledgeBasePage } from '@/modules/desk/knowledge/DeskKnowledgeBasePage';
import { DeskReportsPage } from '@/modules/desk/reports/DeskReportsPage';
import { DeskInboxPage } from '@/modules/desk/inbox/DeskInboxPage';
import { DeskLiveChatPage } from '@/modules/desk/livechat/DeskLiveChatPage';
import { DeskBoardsPage } from '@/modules/desk/boards/DeskBoardsPage';
import { DeskAutomationPage } from '@/modules/desk/automation/DeskAutomationPage';
import { DeskWorkflowDesignerPage } from '@/modules/desk/setup/DeskWorkflowDesignerPage';
import { DeskCannedResponsesPage } from '@/modules/desk/canned/DeskCannedResponsesPage';
import { FinanceDashboardPage } from '@/modules/finance/dashboard/FinanceDashboardPage';
import { FinanceLedgerPage } from '@/modules/finance/ledger/FinanceLedgerPage';
import { FinanceReportsPage } from '@/modules/finance/reports/FinanceReportsPage';
import { FinanceBankingPage } from '@/modules/finance/banking/FinanceBankingPage';
import { FinanceSetupPage } from '@/modules/finance/setup/FinanceSetupPage';
import { FinanceExpensesPage } from '@/modules/finance/expenses/FinanceExpensesPage';
import { FinanceInvoicesPage } from '@/modules/finance/invoices/FinanceInvoicesPage';
import { FinancePaymentsPage } from '@/modules/finance/payments/FinancePaymentsPage';
import { FinanceAccountingPage } from '@/modules/finance/accounting/FinanceAccountingPage';
import { FinanceTaxesPage } from '@/modules/finance/taxes/FinanceTaxesPage';
import { FinanceAssetsPage } from '@/modules/finance/assets/FinanceAssetsPage';
import { FinanceBudgetsPage } from '@/modules/finance/budgets/FinanceBudgetsPage';
import { FinanceApPage } from '@/modules/finance/payables/FinanceApPage';
import { FinanceTreasuryPage } from '@/modules/finance/treasury/FinanceTreasuryPage';
import { FinanceRevenuePage } from '@/modules/finance/revenue/FinanceRevenuePage';
import { FinanceWorkflowsPage } from '@/modules/finance/workflows/FinanceWorkflowsPage';
import { FinanceWorkflowDesignerPage } from '@/modules/finance/workflows/FinanceWorkflowDesignerPage';
import { FinanceReportBuilderPage } from '@/modules/finance/reports/FinanceReportBuilderPage';
import {
  FinanceReceivablesWorkspacePage,
  FinancePayablesWorkspacePage,
  FinanceTaxWorkspacePage,
  FinanceAdministrationWorkspacePage,
} from '@/modules/finance/workspaces/FinanceWorkspacePages';
import { FinanceSetupWizardPage } from '@/modules/finance/administration/FinanceSetupWizardPage';
import { FinanceHealthCenterPage } from '@/modules/finance/administration/FinanceHealthCenterPage';
import { FinanceCloseCenterPage } from '@/modules/finance/administration/FinanceCloseCenterPage';
import { FinanceBusinessRulesPage } from '@/modules/finance/administration/FinanceBusinessRulesPage';
import { FinanceCashForecastPage } from '@/modules/finance/treasury/FinanceCashForecastPage';
import { FinanceCollectionsCenterPage } from '@/modules/finance/collections/FinanceCollectionsCenterPage';
import { FinanceAutomationPage } from '@/modules/finance/automation/FinanceAutomationPage';
import { FinanceAnalyticsPage } from '@/modules/finance/analytics/FinanceAnalyticsPage';
import { FinanceCompliancePage } from '@/modules/finance/compliance/FinanceCompliancePage';
import { FinanceAuditTrailPage } from '@/modules/finance/audit/FinanceAuditTrailPage';
import { FinanceDocumentCapturePage } from '@/modules/finance/ocr/FinanceDocumentCapturePage';
import { FinanceIntegrationsPage } from '@/modules/finance/integrations/FinanceIntegrationsPage';
import { ProjectsDashboardPage } from '@/modules/projects/dashboard/ProjectsDashboardPage';
import { ProjectsListPage } from '@/modules/projects/list/ProjectsListPage';
import { ProjectsTasksPage } from '@/modules/projects/tasks/ProjectsTasksPage';
import { ProjectsBoardsPage } from '@/modules/projects/boards/ProjectsBoardsPage';
import { ProjectsSprintsPage } from '@/modules/projects/sprints/ProjectsSprintsPage';
import { ProjectsIssuesPage } from '@/modules/projects/issues/ProjectsIssuesPage';
import { ProjectsGanttPage } from '@/modules/projects/gantt/ProjectsGanttPage';
import { ProjectsResourcesPage } from '@/modules/projects/resources/ProjectsResourcesPage';
import { WmsMobilePage } from '@/modules/warehouse/mobile/WmsMobilePage';
import { SocialApprovalsPage } from '@/modules/social/approvals/SocialApprovalsPage';
import { MyWorkPage } from '@/pages/work/MyWorkPage';
import { ExecutiveWorkPage } from '@/pages/work/ExecutiveWorkPage';
import { NotificationsCenterPage } from '@/pages/work/NotificationsCenterPage';
import { LeadGenAbTestsPage } from '@/modules/leadgen/LeadGenAbTestsPage';
import { LeadGenWebhooksPage } from '@/modules/leadgen/LeadGenWebhooksPage';
import { LeadGenEnrichmentPage } from '@/modules/leadgen/LeadGenEnrichmentPage';
import { ProjectsCalendarPage } from '@/modules/projects/calendar/ProjectsCalendarPage';
import { ProjectsWorkloadPage } from '@/modules/projects/workload/ProjectsWorkloadPage';
import { ProjectsReportsPage } from '@/modules/projects/reports/ProjectsReportsPage';
import { ProjectsAutomationPage } from '@/modules/projects/automation/ProjectsAutomationPage';
import { ProjectsSetupPage } from '@/modules/projects/setup/ProjectsSetupPage';
import { ProjectsTimesheetsPage } from '@/modules/projects/timesheets/ProjectsTimesheetsPage';
import { ProjectsPortfolioPage } from '@/modules/projects/portfolio/ProjectsPortfolioPage';
import { ProjectClientPortalLoginPage, ProjectClientPortalVerifyPage, ProjectClientPortalDashboardPage } from '@/modules/projects/portal/ProjectClientPortalPages';
import { ProcurementDashboardPage } from '@/modules/procurement/dashboard/ProcurementDashboardPage';
import { PurchaseRequestsPage } from '@/modules/procurement/purchase-requests/PurchaseRequestsPage';
import { RfqsPage } from '@/modules/procurement/rfqs/RfqsPage';
import { PurchaseOrdersPage } from '@/modules/procurement/purchase-orders/PurchaseOrdersPage';
import { ProcurementApprovalsPage } from '@/modules/procurement/approvals/ProcurementApprovalsPage';
import { GoodsReceiptsPage } from '@/modules/procurement/goods-receipts/GoodsReceiptsPage';
import { ProcurementSetupPage } from '@/modules/procurement/setup/ProcurementSetupPage';
import { ProcurementReportsPage } from '@/modules/procurement/reports/ProcurementReportsPage';
import { ContractsPage } from '@/modules/procurement/contracts/ContractsPage';
import { CatalogPage } from '@/modules/procurement/catalog/CatalogPage';
import { VendorPortalLoginPage } from '@/modules/procurement/vendor-portal/VendorPortalLoginPage';
import { VendorPortalVerifyPage } from '@/modules/procurement/vendor-portal/VendorPortalVerifyPage';
import { VendorPortalDashboardPage } from '@/modules/procurement/vendor-portal/VendorPortalDashboardPage';
import { QuotationComparisonPage } from '@/modules/procurement/quotations/QuotationComparisonPage';
import { VendorPaymentsPage } from '@/modules/procurement/vendor-bills/VendorPaymentsPage';
import { PosPage } from '@/pages/pos/PosPage';
import { HeldBillsPage } from '@/pages/pos/HeldBillsPage';
import { PosKeyboardGuidePage } from '@/pages/pos/PosKeyboardGuidePage';
import { PosShiftPage } from '@/pages/pos/PosShiftPage';
import { PosSetupPage } from '@/pages/pos/PosSetupPage';
import { PosReportsPage } from '@/pages/pos/PosReportsPage';
import { PosCustomerDisplayPage } from '@/pages/pos/PosCustomerDisplayPage';
import { PosReturnsPage } from '@/pages/pos/PosReturnsPage';
import { PosAnalyticsDashboard } from '@/pages/pos/PosAnalyticsDashboard';
import { OnboardingPage } from '@/modules/onboarding/OnboardingPage';
import { ProductSelectionPage } from '@/modules/onboarding/ProductSelectionPage';
import { PosOnboardingWizard } from '@/modules/onboarding/PosOnboardingWizard';
import { MODULE_ENTRIES } from '@/config/moduleEntries';
import { moduleUsersSegment } from '@/config/moduleDefaults';
import { LegacyOrgAdminRedirect } from '@/routes/LegacyOrgAdminRedirect';
import { ALL_LEGACY_ORG_REDIRECTS } from '@/config/modulePathMigration';
import { LegacyModulePathRedirect } from '@/routes/LegacyModulePathRedirect';
import { UnknownRouteHandler } from '@/components/routes/UnknownRouteHandler';

export function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<PublicOnlyRoute />}>
          <Route element={<AuthLayout />}>
            <Route path={MODULE_HUB_PATH} element={<FattahLoginPage />} />
            <Route path={LEGACY_MODULE_HUB_PATH} element={<Navigate to={MODULE_HUB_PATH} replace />} />
            <Route path={CHOOSE_APP_PATH} element={<ModuleHubPage />} />
            <Route path={REGISTER_PATH} element={<RegisterPage />} />
            <Route path="/master/login" element={<MasterLoginPage />} />
            <Route path={LEGACY_REGISTER_PATH} element={<Navigate to={REGISTER_PATH} replace />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
            {getAllModuleLoginRoutes().map(({ path, slug }) => (
              <Route key={path} path={path} element={<ModuleLoginPage slug={slug} />} />
            ))}
            {getAllModuleRegisterRoutes().map(({ path, slug }) => (
              <Route key={path} path={path} element={<ModuleRegisterPage slug={slug} />} />
            ))}
          </Route>
        </Route>

        <Route element={<ModuleScopeGuard />}>
        <Route path="/embed/lead/:publicKey" element={<LeadGenPublicSubmitPage />} />
        <Route path="/careers/:orgId" element={<HrPublicCareersPage />} />
        <Route path="/projects-portal/login" element={<ProjectClientPortalLoginPage />} />
        <Route path="/projects-portal/verify" element={<ProjectClientPortalVerifyPage />} />
        <Route path="/projects-portal/dashboard" element={<ProjectClientPortalDashboardPage />} />
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/verify" element={<PortalVerifyPage />} />
        <Route path="/portal/dashboard" element={<PortalDashboardPage />} />
        <Route path="/pos/display/:token" element={<PosCustomerDisplayPage />} />

        <Route element={<ProtectedRoute role="master" />}>
          <Route element={<MasterAdminLayout />}>
            <Route path="/master" element={<MasterDashboardPage />} />
            <Route path="/master/organizations" element={<OrganizationsPage />} />
            <Route path="/master/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/master/plans" element={<PlansPage />} />
            <Route path="/master/analytics" element={<AnalyticsPage />} />
            <Route path="/master/reference-data" element={<MasterReferenceDataPage />} />
            <Route path="/master/custom-plans" element={<MasterCustomPlansPage />} />
            <Route path="/master/settings" element={<MasterSettingsPage />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute role="org" />}>
          <Route path="/onboarding/products" element={<ProductSelectionPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/onboarding/pos" element={<PosOnboardingWizard />} />
          {MODULE_ENTRIES.map((entry) => (
            <Route
              key={`ws-${entry.slug}`}
              path={`/workspace/${entry.slug}`}
              element={<Navigate to={entry.homePath} replace />}
            />
          ))}
          <Route element={<OrgAdminLayout />}>
            <Route path="/org" element={<OrgDashboardPage />} />
            <Route path="/pos/dashboard" element={<OrgDashboardPage />} />
            <Route path="/work" element={<MyWorkPage />} />
            <Route path="/work/executive" element={<ExecutiveWorkPage />} />
            <Route path="/notifications" element={<NotificationsCenterPage />} />
            <Route path="/dashboard/home" element={<Navigate to="/work" replace />} />
            <Route path="/dashboard/pos" element={<Navigate to="/pos/dashboard" replace />} />
            <Route path="/dashboard/crm" element={<Navigate to="/crm" replace />} />
            <Route path="/dashboard/hr" element={<Navigate to="/hr" replace />} />
            <Route path="/dashboard/finance" element={<Navigate to="/finance" replace />} />
            {MODULE_ENTRIES.map((entry) => (
              <Route key={`admin-${entry.slug}`}>
                <Route path={`/${entry.slug}/administration`} element={<AdministrationPage />} />
                <Route path={`/${entry.slug}/profile`} element={<OrganizationProfilePage />} />
                <Route
                  path={`/${entry.slug}/${moduleUsersSegment(entry.erpModule)}`}
                  element={<EmployeesPage />}
                />
                <Route path={`/${entry.slug}/roles`} element={<RolesPage />} />
                <Route path={`/${entry.slug}/settings`} element={<ModuleWorkspaceSettingsPage />} />
                <Route
                  path={`/${entry.slug}/subscription`}
                  element={<ModuleWorkspaceSubscriptionPage />}
                />
              </Route>
            ))}
            <Route path="/org/profile" element={<LegacyOrgAdminRedirect />} />
            <Route path="/org/administration" element={<LegacyOrgAdminRedirect />} />
            <Route path="/org/administration/billing" element={<LegacyOrgAdminRedirect />} />
            <Route path="/org/subscription" element={<LegacyOrgAdminRedirect />} />
            <Route path="/org/workspace/settings" element={<LegacyOrgAdminRedirect />} />
            <Route path="/org/workspace/subscription" element={<LegacyOrgAdminRedirect />} />
            <Route path="/org/settings" element={<LegacyOrgAdminRedirect />} />
            <Route path="/org/employees" element={<LegacyOrgAdminRedirect />} />
            <Route path="/org/roles" element={<LegacyOrgAdminRedirect />} />
            <Route path="/pos/stores" element={<StoresPage />} />
            <Route path="/pos/products" element={<ProductsPage />} />
            <Route path="/pos/categories" element={<CategoriesPage />} />
            <Route path="/pos/brands" element={<BrandsPage />} />
            <Route path="/pos/units" element={<UnitsPage />} />
            <Route path="/pos/taxes" element={<Navigate to="/pos/tax-rules" replace />} />
            <Route path="/pos/tax-rules" element={<TaxRulesPage />} />
            <Route path="/pos/store-products" element={<StoreProductsPage />} />
            <Route path="/pos/invoices" element={<InvoicesPage />} />
            <Route path="/pos/inventory" element={<InventoryPage />} />
            <Route path="/pos/inventory/ledger" element={<InventoryLedgerPage />} />
            <Route path="/warehouse" element={<WarehouseDashboardPage />} />
            <Route path="/warehouse/operations" element={<WarehouseOperationsPage />} />
            <Route path="/warehouse/setup" element={<WarehouseSetupPage />} />
            <Route path="/warehouse/picks" element={<WarehousePicksPage />} />
            <Route path="/warehouse/transfers" element={<WarehouseTransfersPage />} />
            <Route path="/warehouse/cycle-counts" element={<WarehouseCycleCountsPage />} />
            <Route path="/warehouse/waves" element={<WarehouseWavesPage />} />
            <Route path="/warehouse/dock" element={<WarehouseDockPage />} />
            <Route path="/warehouse/cross-dock" element={<WarehouseCrossDockPage />} />
            <Route path="/warehouse/labor" element={<WarehouseLaborPage />} />
            <Route path="/pos/product-alerts" element={<ProductAlertsPage />} />
            <Route path="/pos/suppliers" element={<SuppliersPage />} />
            <Route path="/procurement" element={<ProcurementDashboardPage />} />
            <Route path="/procurement/purchase-requests" element={<PurchaseRequestsPage />} />
            <Route path="/procurement/rfqs" element={<RfqsPage />} />
            <Route path="/procurement/quotations" element={<QuotationComparisonPage />} />
            <Route path="/procurement/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="/procurement/approvals" element={<ProcurementApprovalsPage />} />
            <Route path="/procurement/goods-receipts" element={<GoodsReceiptsPage />} />
            <Route path="/procurement/vendor-bills" element={<VendorPaymentsPage />} />
            <Route path="/procurement/contracts" element={<ContractsPage />} />
            <Route path="/procurement/catalogs" element={<CatalogPage />} />
            <Route path="/procurement/reports" element={<ProcurementReportsPage />} />
            <Route path="/procurement/setup" element={<ProcurementSetupPage />} />
            <Route path="/vendor-portal/login" element={<VendorPortalLoginPage />} />
            <Route path="/vendor-portal/verify" element={<VendorPortalVerifyPage />} />
            <Route path="/vendor-portal/dashboard" element={<VendorPortalDashboardPage />} />
            <Route path="/procurement/settings" element={<Navigate to="/procurement/setup" replace />} />
            <Route path="/pos/customers" element={<CustomersPage />} />
            {MODULE_SHARED_MASTER_ROUTES.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
            <Route path="/crm" element={<CrmDashboardPage />} />
            <Route path="/crm/leads" element={<CrmLeadsPage />} />
            <Route path="/crm/contacts" element={<CrmContactsPage />} />
            <Route path="/crm/companies" element={<CrmCompaniesPage />} />
            <Route path="/crm/deals" element={<CrmDealsPage />} />
            <Route path="/crm/customers/:customerId" element={<Customer360Page />} />
            <Route path="/crm/pipelines" element={<Navigate to="/crm/setup" replace />} />
            <Route path="/crm/followups" element={<CrmWorkspacePage title="Followups" description="Calls, meetings, reminders and next actions" />} />
            <Route path="/crm/tasks" element={<CrmTasksPage />} />
            <Route path="/crm/campaigns" element={<CrmCampaignsPage />} />
            <Route path="/lead-gen" element={<LeadGenDashboardPage />} />
            <Route path="/lead-gen/forms" element={<LeadGenFormsPage />} />
            <Route path="/lead-gen/landing-pages" element={<LeadGenLandingPagesPage />} />
            <Route path="/lead-gen/campaigns" element={<LeadGenCampaignsPage />} />
            <Route path="/lead-gen/visitor-tracking" element={<LeadGenVisitorTrackingPage />} />
            <Route path="/lead-gen/scoring" element={<LeadGenScoringPage />} />
            <Route path="/lead-gen/routing" element={<LeadGenRoutingPage />} />
            <Route path="/lead-gen/consent" element={<LeadGenConsentPage />} />
            <Route path="/lead-gen/reports" element={<LeadGenReportsPage />} />
            <Route path="/lead-gen/sources" element={<LeadGenSourcesPage />} />
            <Route path="/lead-gen/ab-tests" element={<LeadGenAbTestsPage />} />
            <Route path="/lead-gen/webhooks" element={<LeadGenWebhooksPage />} />
            <Route path="/lead-gen/enrichment" element={<LeadGenEnrichmentPage />} />
            <Route path="/marketing" element={<MarketingDashboardPage />} />
            <Route path="/marketing/campaigns" element={<MarketingCampaignsPage />} />
            <Route path="/marketing/segments" element={<MarketingSegmentsPage />} />
            <Route path="/marketing/journeys" element={<MarketingJourneysPage />} />
            <Route path="/marketing/templates" element={<MarketingTemplatesPage />} />
            <Route path="/marketing/lifecycle" element={<MarketingLifecyclePage />} />
            <Route path="/marketing/reports" element={<MarketingReportsPage />} />
            <Route path="/marketing/webhooks" element={<MarketingEspWebhooksPage />} />
            <Route path="/crm/analytics" element={<Navigate to="/crm/reports" replace />} />
            <Route path="/crm/reports" element={<CrmReportsPage />} />
            <Route path="/crm/setup" element={<CrmSetupPage />} />
            <Route path="/crm/setup/oauth/google/callback" element={<CrmGoogleOAuthCallbackPage />} />
            <Route path="/crm/settings" element={<Navigate to="/crm/setup" replace />} />
            <Route path="/social" element={<SocialDashboardPage />} />
            <Route path="/social/inbox" element={<SocialInboxPage />} />
            <Route path="/social/publishing" element={<SocialPublishingCenterPage />} />
            <Route path="/social/calendar" element={<SocialCalendarPage />} />
            <Route path="/social/media" element={<SocialMediaLibraryPage />} />
            <Route path="/social/analytics" element={<SocialAnalyticsCenterPage />} />
            <Route path="/social/automation" element={<SocialAutomationPage />} />
            <Route path="/social/crm-leads" element={<SocialCrmLeadsPage />} />
            <Route path="/social/teams" element={<SocialTeamsPage />} />
            <Route path="/social/connect" element={<SocialConnectionCenterPage />} />
            <Route path="/social/connect/wizard/:platformId" element={<PlatformSetupWizardPage />} />
            <Route path="/social/connect/guide/:platformId" element={<PlatformGuidePage />} />
            <Route path="/social/settings" element={<SocialSettingsHubPage />} />
            <Route path="/social/comments" element={<SocialCommentsHubPage />} />
            <Route path="/social/platforms/:platformId" element={<PlatformWorkspacePage />} />
            <Route path="/social/platforms/:platformId/:tab" element={<PlatformWorkspacePage />} />
            <Route path="/social/channels" element={<SocialChannelsRedirect />} />
            <Route path="/social/listening" element={<SocialListeningPage />} />
            <Route path="/social/campaigns" element={<SocialCampaignsPage />} />
            <Route path="/social/reviews" element={<SocialReviewsPage />} />
            <Route path="/social/reports" element={<Navigate to="/social/analytics" replace />} />
            <Route path="/social/setup" element={<Navigate to="/social/settings" replace />} />
            <Route path="/social/approvals" element={<SocialApprovalsPage />} />
            <Route path="/social/webhooks" element={<Navigate to="/social/settings?tab=webhooks" replace />} />
            <Route path="/hr" element={<HrDashboardPage />} />
            <Route path="/hr/employees" element={<HrEmployee360Page />} />
            <Route path="/hr/employees/:employeeId" element={<HrEmployee360Page />} />
            <Route path="/hr/attendance" element={<HrAttendancePage />} />
            <Route path="/hr/leaves" element={<HrLeavesPage />} />
            <Route path="/hr/leave-policies" element={<HrLeavePoliciesPage />} />
            <Route path="/hr/shifts" element={<HrShiftsPage />} />
            <Route path="/hr/onboarding" element={<HrOnboardingPage />} />
            <Route path="/hr/documents" element={<HrDocumentsPage />} />
            <Route path="/hr/compensation" element={<HrCompensationPage />} />
            <Route path="/hr/travel" element={<HrTravelPage />} />
            <Route path="/hr/succession" element={<HrSuccessionPage />} />
            <Route path="/hr/admin" element={<HrAdminConfigPage />} />
            <Route path="/hr/approvals" element={<HrApprovalsPage />} />
            <Route path="/hr/payroll" element={<HrPayrollPage />} />
            <Route path="/hr/statutory" element={<HrStatutoryPage />} />
            <Route path="/hr/ess" element={<HrEssPortalPage />} />
            <Route path="/hr/recruitment" element={<HrRecruitmentPage />} />
            <Route path="/hr/appraisal" element={<HrAppraisalPage />} />
            <Route path="/hr/feedback360" element={<HrFeedback360Page />} />
            <Route path="/hr/devices" element={<HrDevicesPage />} />
            <Route path="/hr/claims" element={<HrClaimsPage />} />
            <Route path="/hr/training" element={<HrTrainingPage />} />
            <Route path="/hr/reports" element={<HrReportsPage />} />
            <Route path="/hr/workflows" element={<HrWorkflowsPage />} />
            <Route path="/hr/org" element={<HrOrgStructurePage />} />
            <Route path="/hr/mss" element={<HrMssPortalPage />} />
            <Route path="/hr/setup" element={<HrSetupPage />} />
            <Route path="/hr/settings" element={<Navigate to="/hr/setup" replace />} />
            <Route path="/desk" element={<DeskDashboardPage />} />
            <Route path="/desk/tickets" element={<DeskTicketsPage />} />
            <Route path="/desk/tickets/:ticketId" element={<DeskTicketDetailPage />} />
            <Route path="/desk/support-360/:customerId" element={<CustomerSupport360Page />} />
            <Route path="/desk/inbox" element={<DeskInboxPage />} />
            <Route path="/desk/livechat" element={<DeskLiveChatPage />} />
            <Route path="/desk/projects" element={<Navigate to="/projects" replace />} />
            <Route path="/desk/boards" element={<DeskBoardsPage />} />
            <Route path="/desk/knowledgebase" element={<DeskKnowledgeBasePage />} />
            <Route path="/desk/reports" element={<DeskReportsPage />} />
            <Route path="/desk/automation" element={<DeskAutomationPage />} />
            <Route path="/desk/setup/workflows" element={<DeskWorkflowDesignerPage />} />
            <Route path="/desk/canned-responses" element={<DeskCannedResponsesPage />} />
            <Route path="/desk/setup" element={<DeskSetupPage />} />
            <Route path="/desk/settings" element={<Navigate to="/desk/setup" replace />} />
            <Route path="/finance" element={<FinanceDashboardPage />} />
            <Route path="/finance/receivables" element={<FinanceReceivablesWorkspacePage />} />
            <Route path="/finance/payables" element={<FinancePayablesWorkspacePage />} />
            <Route path="/finance/payables/ap" element={<FinanceApPage />} />
            <Route path="/finance/tax" element={<FinanceTaxWorkspacePage />} />
            <Route path="/finance/control" element={<FinanceAdministrationWorkspacePage />} />
            <Route path="/finance/setup/wizard" element={<FinanceSetupWizardPage />} />
            <Route path="/finance/health" element={<FinanceHealthCenterPage />} />
            <Route path="/finance/close" element={<FinanceCloseCenterPage />} />
            <Route path="/finance/control/rules" element={<FinanceBusinessRulesPage />} />
            <Route path="/finance/treasury/forecast" element={<FinanceCashForecastPage />} />
            <Route path="/finance/collections" element={<FinanceCollectionsCenterPage />} />
            <Route path="/finance/automation" element={<FinanceAutomationPage />} />
            <Route path="/finance/analytics" element={<FinanceAnalyticsPage />} />
            <Route path="/finance/compliance" element={<FinanceCompliancePage />} />
            <Route path="/finance/audit" element={<FinanceAuditTrailPage />} />
            <Route path="/finance/documents" element={<FinanceDocumentCapturePage />} />
            <Route path="/finance/integrations" element={<FinanceIntegrationsPage />} />
            <Route path="/finance/accounting" element={<FinanceAccountingPage />} />
            <Route path="/finance/ledger" element={<FinanceLedgerPage />} />
            <Route path="/finance/invoices" element={<FinanceInvoicesPage />} />
            <Route path="/finance/payments" element={<FinancePaymentsPage />} />
            <Route path="/finance/banking" element={<FinanceBankingPage />} />
            <Route path="/finance/taxes" element={<FinanceTaxesPage />} />
            <Route path="/finance/expenses" element={<FinanceExpensesPage />} />
            <Route path="/finance/assets" element={<FinanceAssetsPage />} />
            <Route path="/finance/budgets" element={<FinanceBudgetsPage />} />
            <Route path="/finance/treasury" element={<FinanceTreasuryPage />} />
            <Route path="/finance/revenue" element={<FinanceRevenuePage />} />
            <Route path="/finance/workflows" element={<FinanceWorkflowsPage />} />
            <Route path="/finance/workflows/designer" element={<FinanceWorkflowDesignerPage />} />
            <Route path="/finance/reports" element={<FinanceReportsPage />} />
            <Route path="/finance/reports/builder" element={<FinanceReportBuilderPage />} />
            <Route path="/finance/setup" element={<FinanceSetupPage />} />
            <Route path="/finance/settings" element={<Navigate to="/finance/setup" replace />} />
            <Route path="/projects" element={<ProjectsDashboardPage />} />
            <Route path="/projects/list" element={<ProjectsListPage />} />
            <Route path="/projects/tasks" element={<ProjectsTasksPage />} />
            <Route path="/projects/boards" element={<ProjectsBoardsPage />} />
            <Route path="/projects/sprints" element={<ProjectsSprintsPage />} />
            <Route path="/projects/timesheets" element={<ProjectsTimesheetsPage />} />
            <Route path="/projects/portfolio" element={<ProjectsPortfolioPage />} />
            <Route path="/projects/issues" element={<ProjectsIssuesPage />} />
            <Route path="/projects/gantt" element={<ProjectsGanttPage />} />
            <Route path="/projects/resources" element={<ProjectsResourcesPage />} />
            <Route path="/warehouse/mobile" element={<WmsMobilePage />} />
            <Route path="/projects/calendar" element={<ProjectsCalendarPage />} />
            <Route path="/projects/workload" element={<ProjectsWorkloadPage />} />
            <Route path="/projects/reports" element={<ProjectsReportsPage />} />
            <Route path="/projects/automation" element={<ProjectsAutomationPage />} />
            <Route path="/projects/setup" element={<ProjectsSetupPage />} />
            <Route path="/projects/settings" element={<Navigate to="/projects/setup" replace />} />
            <Route path="/pos/discounts" element={<DiscountsPage />} />
            <Route path="/pos/reports" element={<ReportsPage />} />
            <Route path="/org/locked/:moduleKey" element={<LockedModulePage />} />
            {ALL_LEGACY_ORG_REDIRECTS.map(({ fromPrefix, toPrefix }) => (
              <Route key={`legacy-${fromPrefix}`}>
                <Route path={fromPrefix} element={<Navigate to={toPrefix} replace />} />
                <Route
                  path={`${fromPrefix}/*`}
                  element={<LegacyModulePathRedirect fromPrefix={fromPrefix} toPrefix={toPrefix} />}
                />
              </Route>
            ))}
          </Route>
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route element={<PosLayout />}>
            <Route path="/pos" element={<PosPage />} />
            <Route path="/pos/held" element={<HeldBillsPage />} />
            <Route path="/pos/shortcuts" element={<PosKeyboardGuidePage />} />
            <Route path="/pos/shift" element={<PosShiftPage />} />
            <Route path="/pos/returns" element={<PosReturnsPage />} />
            <Route path="/pos/analytics" element={<PosAnalyticsDashboard />} />
            <Route path="/pos/setup" element={<PosSetupPage />} />
            <Route path="/pos/terminal-reports" element={<PosReportsPage />} />
          </Route>
        </Route>
        </Route>

        <Route path="*" element={<UnknownRouteHandler />} />
      </Routes>
    </BrowserRouter>
  );
}

